/**
 * FetchedRegionsTracker - Implementation of FetchedRegionsTrackerInterface
 *
 * Tracks which regions have been fetched from the backend to avoid
 * unnecessary requests. Uses RBush for efficient rectangle operations.
 *
 * Works with numeric indices (NOT raw values!) for consistency with the rest of the system.
 *
 * @example
 * ```typescript
 * const tracker = new FetchedRegionsTracker()
 *
 * const viewport: ViewportWindow = { spanX: [0, 30], indexRangeY: [0, 10] };
 * tracker.markAsFetched(viewport, false)
 *
 * const toFetch = tracker.getUnfetchedRegions(viewport)
 * ```
 */

import RBush from 'rbush'
import { DateTime } from 'luxon'
import type {
  FetchedRegionsTrackerInterface,
  FetchedRegion,
  FetchedRegionStats
} from './types/fetched-regions-tracker.interface'
import type { Viewport } from '../crosspoint/types/viewport.interface'

interface RBushRegion extends FetchedRegion {
  minX: number // Start index (TXAxisValue-axis)
  maxX: number // End index (TXAxisValue-axis)
  minY: number // Start index (TYAxisValue-axis)
  maxY: number // End index (TYAxisValue-axis)
}

export class FetchedRegionsTracker implements FetchedRegionsTrackerInterface {
  private tree: RBush<RBushRegion>
  private regions: FetchedRegion[]

  constructor() {
    this.tree = new RBush<RBushRegion>()
    this.regions = []
  }

  /**
   * Mark a region as fetched
   */
  markAsFetched(region: Viewport, isEmpty: boolean): void {
    const fetchedRegion: FetchedRegion = {
      xRange: region.indexRangeX,
      yRange: region.indexRangeY,
      fetchedAt: DateTime.now(),
      isEmpty
    }

    // Add to list
    this.regions.push(fetchedRegion)

    // Add to RBush for spatial queries (indices are already numeric!)
    const rbushItem: RBushRegion = {
      ...fetchedRegion,
      minX: Math.min(region.indexRangeX[0], region.indexRangeX[1]),
      maxX: Math.max(region.indexRangeX[0], region.indexRangeX[1]),
      minY: Math.min(region.indexRangeY[0], region.indexRangeY[1]),
      maxY: Math.max(region.indexRangeY[0], region.indexRangeY[1])
    }

    this.tree.insert(rbushItem)
  }

  /**
   * Check if a region has been completely fetched
   */
  isRegionFetched(region: Viewport): boolean {
    const queryBox = {
      minX: Math.min(region.indexRangeX[0], region.indexRangeX[1]),
      maxX: Math.max(region.indexRangeX[0], region.indexRangeX[1]),
      minY: Math.min(region.indexRangeY[0], region.indexRangeY[1]),
      maxY: Math.max(region.indexRangeY[0], region.indexRangeY[1])
    }

    // Find all overlapping fetched regions
    const overlapping = this.tree.search(queryBox)

    // Check if overlapping regions completely cover the query
    return this.isCompletelyCovered(queryBox, overlapping)
  }

  /**
   * Get unfetched regions for a viewport
   */
  getUnfetchedRegions(viewport: Viewport): Viewport[] {
    const queryBox = {
      minX: Math.min(viewport.indexRangeX[0], viewport.indexRangeX[1]),
      maxX: Math.max(viewport.indexRangeX[0], viewport.indexRangeX[1]),
      minY: Math.min(viewport.indexRangeY[0], viewport.indexRangeY[1]),
      maxY: Math.max(viewport.indexRangeY[0], viewport.indexRangeY[1])
    }

    // Find all overlapping fetched regions
    const overlapping = this.tree.search(queryBox)

    // If no overlapping regions, entire viewport is unfetched
    if (overlapping.length === 0) {
      return [viewport]
    }

    // Calculate rectangle difference
    const missing = this.calculateMissingRectangles(queryBox, overlapping, viewport)

    // Safety guard: if the algorithm found no gaps but the region is not fully covered
    // (can happen after invalidateRegion creates non-uniform remainder shapes),
    // conservatively return the full viewport as unfetched to avoid stale data.
    if (missing.length === 0 && !this.isCompletelyCovered(queryBox, overlapping)) {
      return [viewport]
    }

    return missing
  }

  /**
   * Clear all tracked regions
   */
  clear(): void {
    this.tree.clear()
    this.regions = []
  }

  /**
   * Get statistics
   */
  getStats(): FetchedRegionStats {
    const emptyRegions = this.regions.filter(r => r.isEmpty).length
    const dataRegions = this.regions.filter(r => !r.isEmpty).length

    return {
      totalRegions: this.regions.length,
      emptyRegions,
      dataRegions,
      totalArea: this.calculateTotalArea()
    }
  }

  /**
   * Invalidate a previously fetched region using AABB rectangle subtraction.
   *
   * Finds all fetched regions overlapping with the given bounds, removes them,
   * and re-inserts up to 4 remainder rectangles per region (left, right, top, bottom).
   * Both this.tree (RBush) and this.regions (Array) are kept in sync.
   */
  invalidateRegion(bounds: Viewport): void {
    const queryRect = {
      minX: Math.min(bounds.indexRangeX[0], bounds.indexRangeX[1]),
      maxX: Math.max(bounds.indexRangeX[0], bounds.indexRangeX[1]),
      minY: Math.min(bounds.indexRangeY[0], bounds.indexRangeY[1]),
      maxY: Math.max(bounds.indexRangeY[0], bounds.indexRangeY[1])
    }

    const overlapping = this.tree.search(queryRect)

    if (overlapping.length === 0) {
      return
    }

    for (const region of overlapping) {
      // Remove from RBush
      this.tree.remove(region)

      // Remove from this.regions via value-based matching
      const regionIndex = this.regions.findIndex(
        r => r.xRange[0] === region.xRange[0] &&
             r.xRange[1] === region.xRange[1] &&
             r.yRange[0] === region.yRange[0] &&
             r.yRange[1] === region.yRange[1]
      )
      if (regionIndex !== -1) {
        this.regions.splice(regionIndex, 1)
      }

      // Calculate remainder rectangles via AABB subtraction
      const remainders = this.calculateRemainders(region, queryRect)

      // Re-insert each remainder into both tree and regions
      for (const remainder of remainders) {
        const fetchedRegion: FetchedRegion = {
          xRange: [remainder.minX, remainder.maxX],
          yRange: [remainder.minY, remainder.maxY],
          fetchedAt: region.fetchedAt,
          isEmpty: region.isEmpty
        }

        this.regions.push(fetchedRegion)

        const rbushItem: RBushRegion = {
          ...fetchedRegion,
          minX: remainder.minX,
          maxX: remainder.maxX,
          minY: remainder.minY,
          maxY: remainder.maxY
        }

        this.tree.insert(rbushItem)
      }
    }
  }

  /**
   * Calculate up to 4 AABB remainder rectangles from subtracting invalidation bounds from a region.
   *
   * Layout: Left/Right get full Y height, Top/Bottom are clamped to X range of bounds
   * to prevent overlap with Left/Right remainders.
   */
  private calculateRemainders(
    region: RBushRegion,
    bounds: { minX: number; maxX: number; minY: number; maxY: number }
  ): Array<{ minX: number; maxX: number; minY: number; maxY: number }> {
    const remainders: Array<{ minX: number; maxX: number; minY: number; maxY: number }> = []

    // Left remainder (full Y height)
    // Use bounds.minX - 1 because indices are discrete integers (inclusive boundaries)
    if (region.minX < bounds.minX) {
      remainders.push({
        minX: region.minX,
        maxX: bounds.minX - 1,
        minY: region.minY,
        maxY: region.maxY
      })
    }

    // Right remainder (full Y height)
    // Use bounds.maxX + 1 because indices are discrete integers (inclusive boundaries)
    if (region.maxX > bounds.maxX) {
      remainders.push({
        minX: bounds.maxX + 1,
        maxX: region.maxX,
        minY: region.minY,
        maxY: region.maxY
      })
    }

    // Clamped X range for top/bottom (avoids overlap with left/right)
    const clampedLeft = Math.max(region.minX, bounds.minX)
    const clampedRight = Math.min(region.maxX, bounds.maxX)

    // Top remainder (clamped X)
    if (region.minY < bounds.minY && clampedLeft <= clampedRight) {
      remainders.push({
        minX: clampedLeft,
        maxX: clampedRight,
        minY: region.minY,
        maxY: bounds.minY - 1
      })
    }

    // Bottom remainder (clamped X)
    // Use bounds.maxY + 1 because indices are discrete integers (inclusive boundaries)
    if (region.maxY > bounds.maxY && clampedLeft <= clampedRight) {
      remainders.push({
        minX: clampedLeft,
        maxX: clampedRight,
        minY: bounds.maxY + 1,
        maxY: region.maxY
      })
    }

    return remainders
  }

  /**
   * Get all regions
   */
  getAllRegions(): FetchedRegion[] {
    return [...this.regions]
  }

  /**
   * Check if a query box is completely covered by fetched regions
   */
  private isCompletelyCovered(
    queryBox: { minX: number; maxX: number; minY: number; maxY: number },
    fetchedRegions: RBushRegion[]
  ): boolean {
    // Check if any single region covers the entire query
    for (const region of fetchedRegions) {
      if (
        region.minX <= queryBox.minX &&
        region.maxX >= queryBox.maxX &&
        region.minY <= queryBox.minY &&
        region.maxY >= queryBox.maxY
      ) {
        return true
      }
    }

    // Check if the query is fully covered by combination of regions
    // Sort regions by minX for sweep-line algorithm
    const sortedRegions = [...fetchedRegions].sort((a, b) => a.minX - b.minX)

    // For simplicity, check only if all regions have same TYAxisValue range as query
    const sameYRange = sortedRegions.every(r =>
      r.minY <= queryBox.minY && r.maxY >= queryBox.maxY
    )

    if (!sameYRange) return false

    // Check TXAxisValue coverage with sweep line
    let coveredUntil = queryBox.minX

    for (const region of sortedRegions) {
      // If there's a gap, not fully covered
      if (region.minX > coveredUntil) {
        return false
      }
      // Extend coverage
      coveredUntil = Math.max(coveredUntil, region.maxX)
      // If we've covered the entire query, we're done
      if (coveredUntil >= queryBox.maxX) {
        return true
      }
    }

    return coveredUntil >= queryBox.maxX
  }

  /**
   * Calculate missing rectangles using rectangle difference
   */
  private calculateMissingRectangles(
    queryBox: { minX: number; maxX: number; minY: number; maxY: number },
    fetchedRegions: RBushRegion[],
    viewport: Viewport
  ): Viewport[] {
    const missing: Viewport[] = []

    // If no fetched regions, entire viewport is missing
    if (fetchedRegions.length === 0) {
      return [viewport]
    }

    // Sort regions by TXAxisValue-axis (index) for gap detection
    const sortedRegions = [...fetchedRegions].sort((a, b) => a.minX - b.minX)

    // Check if all regions cover the same TYAxisValue range as the query
    const allCoverY = sortedRegions.every(r =>
      r.minY <= queryBox.minY && r.maxY >= queryBox.maxY
    )

    if (allCoverY) {
      // Detect gaps in TXAxisValue (horizontal) dimension using sweep line
      let lastCovered = queryBox.minX

      for (const region of sortedRegions) {
        // Skip regions that don't overlap with query TYAxisValue range
        if (region.maxY < queryBox.minY || region.minY > queryBox.maxY) {
          continue
        }

        // If there's a gap between last covered and this region
        if (region.minX > lastCovered && lastCovered < queryBox.maxX) {
          const gapStart = Math.max(lastCovered, queryBox.minX)
          const gapEnd = Math.min(region.minX, queryBox.maxX)

          if (gapStart < gapEnd) {
            missing.push({
              indexRangeX: [gapStart, gapEnd],
              indexRangeY: viewport.indexRangeY
            })
          }
        }

        // Update last covered position
        lastCovered = Math.max(lastCovered, region.maxX)
      }

      // Check if there's a gap at the end
      if (lastCovered < queryBox.maxX) {
        missing.push({
          indexRangeX: [lastCovered, queryBox.maxX],
          indexRangeY: viewport.indexRangeY
        })
      }

      return missing
    }

    // Fallback to simple bounding box approach for complex cases
    // Find the bounding box of all fetched regions
    let fetchedMinX = Infinity
    let fetchedMaxX = -Infinity
    let fetchedMinY = Infinity
    let fetchedMaxY = -Infinity

    for (const region of fetchedRegions) {
      fetchedMinX = Math.min(fetchedMinX, region.minX)
      fetchedMaxX = Math.max(fetchedMaxX, region.maxX)
      fetchedMinY = Math.min(fetchedMinY, region.minY)
      fetchedMaxY = Math.max(fetchedMaxY, region.maxY)
    }

    // Calculate missing rectangles at edges
    // Earlier TXAxisValue-axis (left)
    if (queryBox.minX < fetchedMinX) {
      missing.push({
        indexRangeX: [queryBox.minX, Math.min(fetchedMinX, queryBox.maxX)],
        indexRangeY: viewport.indexRangeY
      })
    }

    // Later TXAxisValue-axis (right)
    if (queryBox.maxX > fetchedMaxX) {
      missing.push({
        indexRangeX: [Math.max(fetchedMaxX, queryBox.minX), queryBox.maxX],
        indexRangeY: viewport.indexRangeY
      })
    }

    // Top TYAxisValue-axis (above)
    if (queryBox.minY < fetchedMinY && fetchedMinX <= queryBox.maxX && fetchedMaxX >= queryBox.minX) {
      const xStart = Math.max(fetchedMinX, queryBox.minX)
      const xEnd = Math.min(fetchedMaxX, queryBox.maxX)

      // Only add if there's a valid TXAxisValue overlap
      if (xStart < xEnd) {
        missing.push({
          indexRangeX: [xStart, xEnd],
          indexRangeY: [queryBox.minY, Math.min(fetchedMinY, queryBox.maxY)]
        })
      }
    }

    // Bottom TYAxisValue-axis (below)
    if (queryBox.maxY > fetchedMaxY && fetchedMinX <= queryBox.maxX && fetchedMaxX >= queryBox.minX) {
      const xStart = Math.max(fetchedMinX, queryBox.minX)
      const xEnd = Math.min(fetchedMaxX, queryBox.maxX)

      // Only add if there's a valid TXAxisValue overlap
      if (xStart < xEnd) {
        missing.push({
          indexRangeX: [xStart, xEnd],
          indexRangeY: [Math.max(fetchedMaxY, queryBox.minY), queryBox.maxY]
        })
      }
    }

    return missing
  }

  /**
   * Calculate total area covered
   */
  private calculateTotalArea(): number {
    let totalArea = 0

    for (const region of this.regions) {
      const xSize = Math.abs(region.xRange[1] - region.xRange[0])
      const ySize = Math.abs(region.yRange[1] - region.yRange[0])
      totalArea += xSize * ySize
    }

    return totalArea
  }
}
